// 학부모 라우팅 설정

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSupabaseParentAuth } from '../hooks/useSupabaseParentAuth';

// Layout
import ParentLayout from '../components/layout/ParentLayout';

// Pages
import LoginPage from '../pages/LoginPage';
import SupabaseLoginPage from '../pages/SupabaseLoginPage';
import UnifiedLoginPage from '../../auth/pages/UnifiedLoginPage';
import SupabaseKakaoCallbackPage from '../pages/SupabaseKakaoCallbackPage';
import PhoneLinkPage from '../pages/PhoneLinkPage';
import DashboardPage from '../pages/DashboardPage';
import ChildrenPage from '../pages/ChildrenPage';
import AttendancePage from '../pages/AttendancePage';
import SchedulePage from '../pages/SchedulePage';
import PeriodSchedulePage from '../pages/PeriodSchedulePage';
import PaymentPage from '../pages/PaymentPage';
import PenaltyPage from '../pages/PenaltyPage';
import StudyTimePage from '../pages/StudyTimePage';
import SettingsPage from '../pages/SettingsPage';
import NoticeBoardPage from '../pages/NoticeBoardPage';
import CounselingPage from '../pages/CounselingPage';
import ParentSignupWithInvitePage from '../pages/ParentSignupWithInvitePage';
import DemoPage from '../pages/DemoPage';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requirePhoneLink?: boolean;
}

function ProtectedRoute({ children, requirePhoneLink = true }: ProtectedRouteProps) {
  const { isAuthenticated, isPhoneLinked, loading } = useSupabaseParentAuth();

  // 로딩 중일 때는 스피너 표시
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/parent/login" replace />;
  }

  if (requirePhoneLink && !isPhoneLinked) {
    return <Navigate to="/parent/link-phone" replace />;
  }

  return <>{children}</>;
}

export default function ParentRoutes() {
  return (
    <Routes>
      {/* 공개 라우트 - 통합 로그인 (학부모+학생) */}
      <Route path="login" element={<UnifiedLoginPage />} />
      <Route path="signup" element={<ParentSignupWithInvitePage />} />
      <Route path="auth/kakao/callback" element={<SupabaseKakaoCallbackPage />} />
      <Route path="demo" element={<DemoPage />} />

      {/* 전화번호 연결 (인증만 필요) */}
      <Route
        path="link-phone"
        element={
          <ProtectedRoute requirePhoneLink={false}>
            <PhoneLinkPage />
          </ProtectedRoute>
        }
      />

      {/* 인증 필요 라우트 (레이아웃 포함) */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <ParentLayout />
          </ProtectedRoute>
        }
      >
        {/* 기본 경로 - 대시보드로 리다이렉트 */}
        <Route index element={<Navigate to="dashboard" replace />} />

        {/* 메인 페이지들 */}
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="children" element={<ChildrenPage />} />
        <Route path="attendance" element={<AttendancePage />} />
        <Route path="attendance/:childId" element={<AttendancePage />} />
        <Route path="study-time" element={<StudyTimePage />} />
        <Route path="schedule" element={<SchedulePage />} />
        <Route path="schedule/:childId" element={<SchedulePage />} />
        <Route path="period-schedule" element={<PeriodSchedulePage />} />
        <Route path="period-schedule/:childId" element={<PeriodSchedulePage />} />
        <Route path="payment" element={<PaymentPage />} />
        <Route path="penalty" element={<PenaltyPage />} />
        <Route path="notice" element={<NoticeBoardPage />} />
        <Route path="counseling" element={<CounselingPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* 404 - 대시보드로 리다이렉트 */}
      <Route path="*" element={<Navigate to="/parent/dashboard" replace />} />
    </Routes>
  );
}
